import React, { useState } from 'react';
import { usePopup } from '../../context/PopupContext';
import useUnsavedChangesGuard from '../../hooks/useUnsavedChangesGuard';
import useFormDraft from '../../hooks/useFormDraft';
import './AnnouncementEditor.css';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'placement_drive', label: 'Placement Drive' },
  { value: 'company', label: 'Company Update' },
  { value: 'deadline', label: 'Deadline' },
];

// Real admin authoring for the student Placement Hub's announcement feed
// (docs/UI_UX_ARCHITECTURE.md Admin Phase 5 §14) — persists via
// server/controllers/announcementController.js. Students remain read-only
// consumers; this is the only place these rows are ever written.
const AnnouncementEditor = ({ initial, batches, departments = [], onSave, onCancel }) => {
  const { showConfirm } = usePopup();
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body || '');
  const [category, setCategory] = useState(initial?.category || 'general');
  const [pinned, setPinned] = useState(initial?.pinned || false);
  const [batchId, setBatchId] = useState(initial?.batch_id || '');
  const [branch, setBranch] = useState(initial?.branch || '');
  const [expiresAt, setExpiresAt] = useState(initial?.expires_at ? initial.expires_at.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [draftBannerDismissed, setDraftBannerDismissed] = useState(false);

  // audit AD-10: composing an announcement (or editing one) used to have no protection against a
  // refresh/close or an accidental Cancel click discarding everything typed.
  useUnsavedChangesGuard(dirty);
  const draftKey = initial ? `announcement_edit_${initial.id}` : 'announcement_new';
  const { initialDraft, savedAt, clearDraft } = useFormDraft(draftKey, { title, body, category, pinned, batchId, branch, expiresAt }, { enabled: dirty });
  const showDraftBanner = !!initialDraft && !draftBannerDismissed && !dirty;
  const restoreDraft = () => {
    const d = initialDraft.data;
    setTitle(d.title || ''); setBody(d.body || ''); setCategory(d.category || 'general');
    setPinned(!!d.pinned); setBatchId(d.batchId || ''); setBranch(d.branch || ''); setExpiresAt(d.expiresAt || '');
    setDirty(true);
    setDraftBannerDismissed(true);
  };
  const discardDraft = () => { clearDraft(); setDraftBannerDismissed(true); };

  const handleCancel = async () => {
    if (dirty) {
      const ok = await showConfirm('Discard this announcement?', 'You have unsaved changes. Leaving now will discard them.');
      if (!ok) return;
    }
    clearDraft();
    onCancel();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        body: body.trim(),
        category,
        pinned,
        batch_id: batchId || null,
        branch: branch || null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      });
      clearDraft();
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="ann-editor" onSubmit={submit}>
      {showDraftBanner && (
        <div className="ann-draft-banner" role="status">
          <span>You have an unsaved draft from {new Date(initialDraft.savedAt).toLocaleString()}.</span>
          <span className="ann-draft-banner-actions">
            <button type="button" className="ann-btn-save" onClick={restoreDraft}>Restore</button>
            <button type="button" className="ann-btn-cancel" onClick={discardDraft}>Discard</button>
          </span>
        </div>
      )}
      <label className="ann-field">
        <span>Title</span>
        <input value={title} onChange={e => { setTitle(e.target.value); setDirty(true); }} required maxLength={140} />
      </label>
      <label className="ann-field">
        <span>Message</span>
        <textarea value={body} onChange={e => { setBody(e.target.value); setDirty(true); }} required rows={4} />
      </label>
      <div className="ann-field-row">
        <label className="ann-field">
          <span>Category</span>
          <select value={category} onChange={e => { setCategory(e.target.value); setDirty(true); }}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>
      </div>
      <div className="ann-field-row">
        <label className="ann-field">
          <span>Department</span>
          <select value={branch} onChange={e => { setBranch(e.target.value); setDirty(true); }}>
            <option value="">All departments</option>
            {departments.map(d => <option key={d.branch} value={d.branch}>{d.branch} ({d.students})</option>)}
          </select>
        </label>
        <label className="ann-field">
          <span>Batch</span>
          <select value={batchId} onChange={e => { setBatchId(e.target.value); setDirty(true); }}>
            <option value="">All batches</option>
            {(batches || []).map(b => <option key={b.id} value={b.id}>{b.name}{b.student_count != null ? ` (${b.student_count})` : ''}</option>)}
          </select>
        </label>
      </div>
      <p className="ann-audience" role="status">
        Audience: <b>{[branch && `${branch} department`, batchId && `batch ${(batches || []).find(b => b.id === batchId)?.name || ''}`].filter(Boolean).join(' · ') || 'Whole college'}</b>
        {branch && batchId ? ' — only students in both.' : ''}
      </p>
      <div className="ann-field-row">
        <label className="ann-field">
          <span>Expires (optional)</span>
          <input type="date" value={expiresAt} onChange={e => { setExpiresAt(e.target.value); setDirty(true); }} />
        </label>
        <label className="ann-checkbox">
          <input type="checkbox" checked={pinned} onChange={e => { setPinned(e.target.checked); setDirty(true); }} />
          <span>Pin to top</span>
        </label>
      </div>
      <div className="ann-actions">
        {savedAt && <span className="ann-draft-saved">Draft autosaved {new Date(savedAt).toLocaleTimeString()}</span>}
        <button type="button" className="ann-btn-cancel" onClick={handleCancel}>Cancel</button>
        <button type="submit" className="ann-btn-save" disabled={saving || !title.trim() || !body.trim()}>
          {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Publish')}
        </button>
      </div>
    </form>
  );
};

export default AnnouncementEditor;
